import { EXTENDED_ART_URL } from "../../context/paths";

const headers = (token) => ({
    "Content-Type": "application/json",
    "authorization": `Bearer ${token}`,
});

const request = async (url, options) => {
    try {
        const response = await fetch(url, options);
        return await response.json();
    } catch (error) {
        return { type: "error", message: error.message };
    }
};

const get = (token, url) => request(url, { method: "GET", headers: headers(token) });
const send = (token, method, url, body) =>
    request(url, { method, headers: headers(token), ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

const ORDERS = `${EXTENDED_ART_URL}/orders`;
const SERIALS = `${EXTENDED_ART_URL}/serials`;

export const createExtendedArtOrder = (token, body) => send(token, "POST", ORDERS, body);
export const getExtendedArtOrder = (token, id) => get(token, `${ORDERS}/${id}`);
export const updateExtendedArtOrder = (token, id, body) => send(token, "PUT", `${ORDERS}/${id}`, body);
export const calcExtendedArtOrder = (token, body) => send(token, "POST", `${ORDERS}/calc`, body);

export const createExtendedArtDraft = (token, body) => send(token, "POST", `${SERIALS}/drafts`, body);
export const finalizeExtendedArtSerial = (token, id, body) => send(token, "POST", `${SERIALS}/${id}/finalize`, body);
export const updateExtendedArtSerial = (token, id, body) => send(token, "PUT", `${SERIALS}/${id}`, body);
export const deleteExtendedArtSerial = (token, id) => send(token, "DELETE", `${SERIALS}/${id}`);
export const getExtendedArtSerialsByIds = (token, ids) => send(token, "POST", `${SERIALS}/by-ids`, { ids });
export const getExtendedArtSerialsByNumbers = (token, serialNumbers) =>
    send(token, "POST", `${SERIALS}/by-numbers`, { serialNumbers });

// Rotas espelhadas do pedido normal (spec 2026-10-01 §4): mesmo corpo e mesma resposta
// de products/functions.js; só o id vai no caminho.
export const updateExtendedArtOrderStatus = (token, id, { status, notify, statusMessage, returnCode = "", trackingCode = "", userEmail }) =>
    send(token, "PUT", `${ORDERS}/${id}/status`, { status, notify, statusMessage, returnCode, trackingCode, userEmail });
export const postExtendedArtComment = (token, id, comment) => send(token, "POST", `${ORDERS}/${id}/comments`, { comment });
export const restoreExtendedArtHistory = (token, id, index) => send(token, "POST", `${ORDERS}/${id}/history/${index}/restore`);

export const presignExtendedArtAttachment = (token, id, body) => send(token, "POST", `${ORDERS}/${id}/attachments/presign`, body);
export const completeExtendedArtAttachment = (token, id, attachmentId) =>
    send(token, "POST", `${ORDERS}/${id}/attachments/complete`, { attachmentId });
export const downloadExtendedArtAttachment = (token, id, attachmentId) =>
    get(token, `${ORDERS}/${id}/attachments/${attachmentId}/download`);
export const renameExtendedArtAttachment = (token, id, attachmentId, name) =>
    send(token, "PUT", `${ORDERS}/${id}/attachments/${attachmentId}`, { name });
export const deleteExtendedArtAttachment = (token, id, attachmentId) =>
    send(token, "DELETE", `${ORDERS}/${id}/attachments/${attachmentId}`);

/** Grava `tagGeneratedAt = agora` nos seriais estendidos que saíram no ZIP (spec P8). */
export const markExtendedArtTagsGenerated = (token, ids) => send(token, "POST", `${SERIALS}/tag-generated`, { ids });

/** Mensagens pt-BR por `code` do backend (contrato 00 + endpoints de arte). */
export const EXTENDED_ART_ERROR_MESSAGES = {
    EXTENDED_ART_ORDER_NOT_FOUND: "Pedido de arte estendida não encontrado.",
    EXTENDED_ART_SERIAL_NOT_FOUND: "Item de arte estendida não encontrado.",
    ART_REQUIRED: "Escolha a arte da carta antes de continuar.",
    ORDER_FULL: "O pedido já tem todas as cartas informadas.",
    CONDITION_REQUIRED: "Informe a condição da carta.",
    AUTOGRAPH_NOT_ALLOWED: "Este pedido não tem avaliação de autógrafo.",
    INVALID_AUTOGRAPH_NOTE: "Nota de autógrafo inválida. Use Autêntico, 8, 9 ou 10.",
    SERIAL_NUMBER_CONFLICT: "Não foi possível gerar o número de série agora. Tente novamente.",
    NUMBER_OF_CARDS_BELOW_ITEMS: "A quantidade de cartas não pode ser menor que o número de itens já cadastrados.",
    INVALID_UPLOAD: "Imagem inválida. Envie um arquivo do tipo correto, de até 10 MB.",
    CARD_SEARCH_UNAVAILABLE: "A busca de cartas está indisponível no momento. Tente novamente em instantes.",
    INVALID_KIND: "Tipo de pedido inválido.",
    INVALID_ART_KEY: "Arte inválida. Escolha a arte novamente.",
    NO_SERIAL_INITIAL: "O modelo não tem inicial de serial cadastrada.",
    GRADABLE_MODEL_NOT_FOUND: "Modelo de carta não encontrado.",
    ART_LABELS_NOT_CONFIGURED: "O armazenamento de artes não está configurado.",
    ITEMS_NOT_FINALIZED: "Finalize todos os itens (condição e, quando houver avaliação, nota de autógrafo) antes de marcar como avaliado",
    HISTORY_ENTRY_NOT_FOUND: "Entrada do histórico não encontrada",
    NOTHING_TO_RESTORE: "Não há o que restaurar nesta entrada",
    UNSUPPORTED_LANG: "Idioma não suportado pelo TCGdex",
    CARD_NOT_FOUND: "Carta não encontrada no TCGdex",
};

/** Mensagens do backend que chegam sem `code`, traduzidas pelo texto. */
export const EXTENDED_ART_MESSAGE_TRANSLATIONS = {
    "Deadline exceeds limit": "Prazo excede o limite da prioridade",
    "Invalid numberOfCards": "Quantidade de cartas inválida",
};

const NETWORK_ERROR_RE = /failed to fetch|networkerror|network request failed|load failed|typeerror/i;

/** Envelope sem `code` cujo texto é de erro de rede cru (fetch rejeitado), não de negócio. */
export const isRawNetworkError = (out) => !out?.code && NETWORK_ERROR_RE.test(String(out?.message || ""));

/** Mensagem do envelope de erro; falha de rede crua vira o texto fixo pt-BR. */
export const extendedArtOutMessage = (out, fallback) =>
    isRawNetworkError(out) ? fallback : extendedArtErrorMessage(out?.code, out?.message) || fallback;

export const extendedArtErrorMessage = (code, fallback = "") =>
    EXTENDED_ART_ERROR_MESSAGES[code] || EXTENDED_ART_MESSAGE_TRANSLATIONS[fallback] || fallback;
